import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { Column } from '@tanstack/react-table';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: React.ReactNode;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  const sorted = column.getIsSorted();
  const sortIndex = column.getSortIndex();

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <Button
        variant='ghost'
        size='sm'
        className='-ml-3 h-8 data-[state=open]:bg-accent'
        onClick={(event) => {
          const multi = event.shiftKey;
          if (sorted === 'asc') {
            column.toggleSorting(true, multi); // Switch to descending
          } else if (sorted === 'desc') {
            column.clearSorting(); // Clear sorting
          } else {
            column.toggleSorting(false, multi); // Start with ascending
          }
        }}
      >
        <span>{title}</span>
        {sorted === 'desc'
          ? <ArrowDown className='ml-2 h-4 w-4' data-testid='sort-desc-icon' />
          : sorted === 'asc'
          ? <ArrowUp className='ml-2 h-4 w-4' data-testid='sort-asc-icon' />
          : <ChevronsUpDown className='ml-2 h-4 w-4' />}
        {sortIndex > 0 && (
          <span
            className='ml-0.5 text-xs text-muted-foreground'
            data-testid='sort-priority'
          >
            {sortIndex + 1}
          </span>
        )}
      </Button>
    </div>
  );
}
